/**
 * FaceImageThumbnail - Individual thumbnail in the face gallery
 * Displays captured image with rotation badge, clickable to open lightbox
 */

import { cn } from '@/lib/utils';
import type { FaceImage } from '@/stores/diceRegistryStore';

/**
 * Props for FaceImageThumbnail component
 */
interface FaceImageThumbnailProps {
  /** Image data to display */
  image: FaceImage;
  /** Callback when thumbnail is clicked (opens lightbox) */
  onClick: () => void;
}

/**
 * Individual thumbnail component for captured face images.
 * Shows image preview with rotation badge and hover effect.
 */
export function FaceImageThumbnail({ image, onClick }: FaceImageThumbnailProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'relative flex-shrink-0 w-16 h-16 rounded-md overflow-hidden cursor-pointer',
        'border-2 transition-all duration-200',
        'border-border hover:border-primary/50',
        'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2'
      )}
      aria-label={`Image captured at rotation ${image.rotationIndex + 1}`}
    >
      {/* Thumbnail image */}
      <img
        src={image.thumbnailUrl}
        alt={`Captured at rotation ${image.rotationIndex + 1}`}
        className="w-full h-full object-cover"
      />

      {/* Rotation indicator badge */}
      <div
        className={cn(
          'absolute bottom-0.5 left-0.5 px-1 py-0.5 rounded text-[10px]',
          'bg-black/60 text-white font-mono'
        )}
      >
        R{image.rotationIndex + 1}
      </div>
    </button>
  );
}

export default FaceImageThumbnail;
